/**
 * ManualEntryModal — ручная операция по лицевому счёту.
 *
 * Нужна там, где автоматики нет: клиника перевела по безналу, мы
 * договорились о скидке, списали лишнее. Это единственное место, где
 * баланс меняет человек, поэтому форма намеренно неудобна в мелочах:
 *
 *   • направление — две явные кнопки, а не минус в сумме. Минус легко
 *     не заметить, а «+30 000» вместо «−30 000» — это разница в
 *     шестьдесят тысяч на счёте живого клиента;
 *   • комментарий обязателен (бэк требует от трёх символов). Через
 *     месяц «корректировка 5000» без пояснения не отличить от ошибки;
 *   • сразу показываем, каким станет остаток. Ошибку в разряде видно
 *     именно здесь, а не после сохранения.
 */

import {useEffect, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';

import {addLedgerEntry, type LedgerKind} from '@services/ledger';
import {keys} from '@queries/keys';
import {Field} from '@components/FormKit';
import {cn} from '@lib/utils';
import {extractErrorMessage} from '@utils/httpError';

import {EntryAmountField} from '../EntryAmountField';
import {ModalShell} from '../ModalShell';

import {ENTRY_KINDS, nextBalanceTiyin, toTiyin} from './ManualEntryModal.utils';

const FAILED = 'Не удалось провести операцию';

type Props = {
  open: boolean;
  clientId: string;
  clientName: string;
  balanceTiyin: number;
  onClose: () => void;
  onSaved: (text: string) => void;
};

export const ManualEntryModal = ({
  open,
  clientId,
  clientName,
  balanceTiyin,
  onClose,
  onSaved,
}: Props) => {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<LedgerKind>('credit');
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');

  const amountTiyin = toTiyin(amount);
  const nextBalance = nextBalanceTiyin(balanceTiyin, kind, amountTiyin);
  const ready = amountTiyin > 0 && comment.trim().length >= 3;

  const mutation = useMutation({
    mutationFn: addLedgerEntry,
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: keys.ledger.client(clientId)});
      /* Остаток виден и в карточке клиента, и в списке — перечитываем. */
      void queryClient.invalidateQueries({queryKey: keys.clients.all});
      onSaved(kind === 'credit' ? 'Счёт пополнен' : 'Списано со счёта');
    },
  });

  /* Сбрасываем и поля, и саму мутацию. Без `reset()` красная плашка
     «не удалось провести операцию» от прошлого клиента встречала бы
     человека в только что открытой форме по другому. */
  useEffect(() => {
    if (!open) return;
    setKind('credit');
    setAmount('');
    setComment('');
    mutation.reset();
    // mutation пересоздаётся каждый рендер — зависим только от open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = () =>
    mutation.mutate({clinicId: clientId, kind, amountTiyin, comment: comment.trim()});

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Операция по счёту"
      subtitle={
        <>
          Лицевой счёт <span className="font-semibold text-foreground">«{clientName}»</span>.
          Операция попадёт в журнал и в аудит — отменить её нельзя, только провести обратную.
        </>
      }
      submitLabel={kind === 'credit' ? 'Пополнить' : 'Списать'}
      onSubmit={handleSubmit}
      submitDisabled={!ready}
      pending={mutation.isPending}
      error={mutation.isError ? extractErrorMessage(mutation.error, FAILED) : null}
      danger={kind === 'debit'}
    >
      <div className="flex gap-2">
        {ENTRY_KINDS.map((k) => (
          <button
            key={k.key}
            type="button"
            onClick={() => setKind(k.key)}
            className={cn(
              'flex-1 rounded-[11px] border p-[10px_12px] text-left transition',
              kind === k.key ? 'border-primary bg-primary/10' : 'border-skr-line',
            )}
          >
            <div className={cn('text-[13.5px] font-semibold', kind === k.key && 'text-primary')}>
              {k.label}
            </div>
            <div className="mt-0.5 text-[11.5px] text-muted-foreground">{k.hint}</div>
          </button>
        ))}
      </div>

      <EntryAmountField
        value={amount}
        onChange={setAmount}
        amountTiyin={amountTiyin}
        nextBalanceTiyin={nextBalance}
      />

      <Field label="Комментарий (попадёт в журнал)" className="mt-3.5">
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, 500))}
          placeholder="Например: перевод по счёту №12 от 3 октября"
          rows={3}
          className="w-full resize-none rounded-[10px] border border-input bg-card p-[11px_13px] text-sm leading-[1.5] outline-none transition focus:border-primary focus:ring-[3px] focus:ring-primary/12"
        />
      </Field>
    </ModalShell>
  );
};
